import { useState, useCallback } from 'react'
import ExpensesService from '@/services/ExpensesService'

export default function useExpensesData() {
	const defaultLimit = 25
	const limitIncrement = 25
	const defaultSortColumn = 'expenseDate'
	const defaultSortDirection = 'desc'

	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)
	const [sortColumn, setSortColumn] = useState(defaultSortColumn)
	const [sortDirection, setSortDirection] = useState(defaultSortDirection)
	const [searchQuery, setSearchQuery] = useState('')
	const [filters, setFilters] = useState({})

	const loadData = useCallback(async () => {
		if (!loadingMore) setLoading(true)
		setError(null)

		// console.log(`TCL -> useExpensesData -> load data filters:`, filters)

		const result = await ExpensesService.getExpensesWithFilters({
			limit,
			sortColumn,
			sortDirection,
			filters: {
				...filters,
				searchText: searchQuery,
			},
		})

		if (result.error) {
			setError(result.error)
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} else {
			setData(result.data)
			setTotalCount(result.totalCount)
			setHasMore(result.hasMore)
		}

		setLoading(false)
		setLoadingMore(false)
	}, [filters, limit, loadingMore, searchQuery, sortColumn, sortDirection])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMore) {
			setLimit(prev => prev + limitIncrement)
			setLoadingMore(true)
		}
	}, [hasMore, limitIncrement, loadingMore])

	const handleSearch = useCallback(value => {
		setSearchQuery(value)
	}, [])

	const setSorting = useCallback((column, direction) => {
		setSortColumn(column)
		setSortDirection(direction)
		setLoading(true)
	}, [])

	const updateFilters = useCallback(newFilters => {
		setFilters(prev => ({ ...prev, ...newFilters }))
	}, [])

	const resetFilters = useCallback(() => {
		setFilters({})
		setSortColumn(defaultSortColumn)
		setSortDirection(defaultSortDirection)
		setLimit(defaultLimit)
	}, [])

	const clearError = useCallback(() => setError(null), [])

	return {
		data,
		totalCount,
		hasMore,
		loading,
		loadingMore,
		error,
		sortColumn,
		sortDirection,
		searchQuery,
		filters,
		loadData,
		handleLoadMore,
		handleSearch,
		setSorting,
		updateFilters,
		resetFilters,
		setError: clearError,
	}
}
